import { Component, EventEmitter, inject, Output } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { allItemsPayload, CustomModalService } from '@piqet-angular/shared';
import { AdministratorService } from 'libs/data/src/lib/services/administrator/administrator.service';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of } from 'rxjs';

@Component({
  selector: 'piqet-angular-admin-copy-organization',
  templateUrl: './admin-copy-organization.component.html',
  styleUrls: ['./admin-copy-organization.component.scss'],
})
export class AdminCopyOrganizationComponent {
  modalLabel: any;
  scheduledDateTime: any;
  organizationForm!: FormGroup;
  organizationList: any;
  filteredOrganizationListOne: any[] = [];
  filteredOrganizationListTwo: any[] = [];
  submitted: boolean = false;
  datePickerConfig = {
    containerClass: 'organizationDatePicker ',
    showWeekNumbers: false,
    dateInputFormat: 'DD/MM/YYYY',
  };
  @Output() saveCopyAdminOrganizationEvent = new EventEmitter<any>();
  convertedTime: any;
  validationMsg: string = '';
  errorTable: any;
  minDate: any;
  toaster = inject(ToastrService);
  sharedService = inject(SharedService);
  router = inject(Router);
  bsValue = new Date();
  timeVal: any;

  constructor(
    private modal: CustomModalService,
    private formBuilder: FormBuilder,
    private administratorService: AdministratorService
  ) {
    this.modalLabel = this.modal?.modalService?.config?.initialState?.['label'];
    this.organizationList = this.modal?.modalService?.config?.initialState?.['organizationList'];
    this.sharedService.toggleMergeOrgList(true);
  }

  ngOnInit() {
    this.organizationForm = this.formBuilder.group({
      organizationOne: [null, Validators.required],
      organizationTwo: [null, [Validators.required]],
    });

    this.filteredOrganizationListOne = this.organizationList;
    this.filteredOrganizationListTwo = this.organizationList;

    this.organizationForm.get('organizationOne')?.valueChanges.subscribe(value => {
      this.filterOrganizationLists();
    });

    this.organizationForm.get('organizationTwo')?.valueChanges.subscribe(value => {
      this.filterOrganizationLists();
    });

    this.minDate = new Date();
    this.bsValue.setDate(this.bsValue.getDate() + 1);
    this.timeVal = '00:00';
    this.convertedTime = '12:00 AM';
  }

  filterOrganizationLists() {
    const selectedOrgsOne = this.organizationForm.get('organizationOne')?.value || [];
    const selectedOrgTwo = this.organizationForm.get('organizationTwo')?.value;

    // Filter out selected "To" organization from the "From" list
    this.filteredOrganizationListOne = this.organizationList.filter(
      (org: any) =>
        org.organizationId !== selectedOrgTwo?.organizationId && !selectedOrgsOne.includes(org)
    );

    // Filter out selected "From" organizations from the "To" list
    this.filteredOrganizationListTwo = this.organizationList.filter(
      (org: any) =>
        !selectedOrgsOne.some(
          (selectedOrg: any) => selectedOrg.organizationId === org.organizationId
        )
    );
  }

  commonInputSearch(term: string, item: any) {
    return item.name.toLowerCase().includes(term.toLowerCase());
  }

  onSave() {
    this.submitted = true;
    if (this.organizationForm.valid) {
      const selectedOrgsOne = this.organizationForm
        .get('organizationOne')
        ?.value.map((org: any) => org.organizationId)
        .join(',');
      let preFormatDate = this.scheduledDateTime;
      preFormatDate = preFormatDate + ' ' + this.convertedTime;
      const data = {
        fromOrganizationIds: selectedOrgsOne, // Send comma-separated IDs
        toOrganizationId: this.organizationForm.get('organizationTwo')?.value.organizationId,
        scheduleDate: preFormatDate,
      };
      this.saveOrganization(data);
    }
  }

  saveOrganization(data: any) {
    this.administratorService
      .copyOrganization(data.fromOrganizationIds, data.toOrganizationId, data.scheduleDate)
      .pipe(
        catchError((error: any) =>
          of(this.toaster.error('An error occurred. Please try again later.'))
        )
      )
      .subscribe(res => {
        if (res.messageType) {
          this.validationMsg = res.message;
          this.errorTable = res.data;
          this.submitted = false;
        } else {
          this.toaster.success(res.message);
          this.modal.close();
          this.sharedService.toggleMergeOrgList(false);
        }
      });
  }

  async getOrganizationList() {
    const organizationList = (
      await this.administratorService.getEntireOrganizationList(allItemsPayload)
    )?.data?.data;
    this.organizationList = organizationList;
    this.filteredOrganizationListOne = organizationList;
    this.filteredOrganizationListTwo = organizationList;
    this.organizationForm.get('organizationOne')?.reset();
    this.organizationForm.get('organizationTwo')?.reset();
  }

  onClose() {
    this.modal.close();
    this.sharedService.toggleMergeOrgList(false);
  }

  onValueChange(event: any) {
    this.scheduledDateTime = this.convertDate(event);
  }

  convertDate(inputDate: any) {
    const pad = (num: number) => num.toString().padStart(2, '0');

    const day = pad(inputDate.getDate());
    const month = pad(inputDate.getMonth() + 1); // getMonth() returns 0-based index
    const year = inputDate.getFullYear();

    return `${month}/${day}/${year}`;
  }

  onTimeChange(event: any) {
    const time = event.target.value; // Get the value in HH:MM format (24-hour)
    this.convertedTime = this.convertTo12HourFormat(time);
  }

  convertTo12HourFormat(time: string): string {
    const [hours, minutes] = time.split(':').map(Number); // Split and convert to numbers
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12; // Convert 24-hour to 12-hour format (12-hour is special)
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  }
}
